import { Link } from "react-router-dom";
import { Facebook, Instagram, Twitter, Youtube, Linkedin, Music2, Link as LinkIcon, MapPin, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FooterMarquee } from "@/components/layout/FooterMarquee";

const SOCIAL_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  x: Twitter,
  youtube: Youtube,
  linkedin: Linkedin,
  tiktok: Music2,
};

// Fallback link columns (used only if settings has none, e.g. very old data)
const FALLBACK_COLUMNS = [
  { title: "Legal & Policies", links: [
    { label: "Privacy Policy", url: "/policy/privacy", external: false },
    { label: "Terms & Conditions", url: "/policy/terms", external: false },
    { label: "Refund Policy", url: "/policy/refund", external: false },
  ]},
  { title: "Get Started", links: [
    { label: "Book a Session", url: "/booking", external: false },
    { label: "Support Ticket", url: "/support", external: false },
    { label: "Client Login", url: "/login", external: false },
  ]},
];

const FooterLink = ({ link }) => {
  const cls = "text-sm text-white/60 hover:text-white transition-colors";
  if (link.external || /^https?:\/\//.test(link.url || "")) {
    return <a href={link.url || "#"} target="_blank" rel="noopener noreferrer" className={cls}>{link.label}</a>;
  }
  return <Link to={link.url || "#"} className={cls}>{link.label}</Link>;
};

export const Footer = () => {
  const { settings } = useSite();
  const year = new Date().getFullYear();
  const brand = settings.brand_name || "Trilogy Sports & Fitness";
  const copyright = (settings.footer_copyright || "").trim()
    ? settings.footer_copyright.replace(/\{year\}/g, year).replace(/\{brand\}/g, brand)
    : `© ${year} ${brand}. All rights reserved.`;
  const locations = settings.footer_locations || "Lenexa, KS · Independence, MO";

  // Socials: prefer the editable list, fall back to legacy social{} object
  const socials = (Array.isArray(settings.footer_socials) && settings.footer_socials.length)
    ? settings.footer_socials
    : Object.entries(settings.social || {}).map(([platform, url]) => ({ platform, url }));

  const columns = (Array.isArray(settings.footer_columns) && settings.footer_columns.length)
    ? settings.footer_columns
    : FALLBACK_COLUMNS;

  const logoWidth = Number(settings.footer_logo_width) || 384;

  return (
    <footer className="footer-angle relative bg-black text-white">
      <FooterMarquee />
      <div className="mx-auto grid w-full max-w-7xl gap-10 px-4 sm:px-6 pb-14 pt-28 md:grid-cols-4">
        <div className="md:col-span-2">
          <img src={settings.logo_url} alt={brand} data-testid="footer-logo" className="h-auto" style={{ width: `${logoWidth}px`, maxWidth: "100%" }} />
          <p className="mt-4 max-w-sm text-sm text-white/60 leading-relaxed">
            {settings.tagline}.<br />{settings.footer_note || "We work with you to plan and produce the perfect peace you need."}
          </p>
          {settings.footer_paragraph && (
            <p className="mt-4 max-w-sm whitespace-pre-wrap text-sm text-white/50 leading-relaxed" data-testid="footer-paragraph">{settings.footer_paragraph}</p>
          )}
          {socials.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="footer-socials">
              {socials.filter((s) => s && s.url).map((s) => {
                const Icon = SOCIAL_ICONS[(s.platform || "").toLowerCase()] || LinkIcon;
                return (
                  <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform || "Social link"} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/15 hover:border-brand hover:text-brand transition-colors">
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          )}
          <div className="mt-5 space-y-2 text-sm text-white/60">
            {settings.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand" />{settings.phone}</p>}
            {settings.email && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand" />{settings.email}</p>}
          </div>
        </div>

        {columns.map((col, ci) => (
          <div key={col.title} data-testid={`footer-column-${ci}`}>
            <h4 className="display-title text-lg">{col.title}</h4>
            <ul className="mt-4 space-y-2">
              {(col.links || []).map((l) => (
                <li key={l.url || l.label}><FooterLink link={l} /></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-2 px-4 sm:px-6 pt-6 pb-24 lg:pb-6 text-xs text-white/40 sm:flex-row">
          <p>{copyright}</p>
          <p className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {locations}</p>
        </div>
      </div>
    </footer>
  );
};
